import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useAudio } from '@/contexts/AudioContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import {
  Radio,
  Play,
  Pause,
  Volume2,
  VolumeX,
  RefreshCw,
  Loader2,
  MapPin,
  Signal,
  ChevronRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Scanner Feed Card
const ScannerFeedCard = ({ scanner, isPlaying, onPlay, onStop }) => {
  const typeColors = {
    police: 'bg-blue-500',
    fire: 'bg-red-500',
    ems: 'bg-green-500',
    default: 'bg-teal-500'
  };
  
  const dotColor = typeColors[scanner.type] || typeColors.default;
  
  return (
    <div 
      className={`p-3 rounded-lg border transition-all cursor-pointer ${
        isPlaying 
          ? 'bg-teal-950/50 border-teal-500/50' 
          : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
      }`}
      onClick={() => isPlaying ? onStop() : onPlay(scanner)}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {/* Status indicator */}
          <div className="relative">
            <div className={`w-3 h-3 rounded-full ${dotColor} ${isPlaying ? 'animate-pulse' : ''}`} />
            {isPlaying && (
              <div className={`absolute inset-0 w-3 h-3 rounded-full ${dotColor} animate-ping opacity-75`} />
            )}
          </div>
          
          {/* Info */}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">{scanner.name}</p>
            <div className="flex items-center gap-2 text-xs text-slate-400">
              <MapPin className="w-3 h-3" />
              <span>{scanner.city}, {scanner.state}</span>
              <span className="text-slate-600">•</span>
              <span className="capitalize">{scanner.type}</span>
            </div>
          </div>
        </div>
        
        {/* Play/Pause button */}
        <button
          className={`p-2 rounded-full transition-colors ${
            isPlaying 
              ? 'bg-teal-500 text-white' 
              : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
          }`}
        >
          {isPlaying ? (
            <Pause className="w-4 h-4" />
          ) : (
            <Play className="w-4 h-4" />
          )}
        </button>
      </div>
      
      {/* Now playing indicator */}
      {isPlaying && (
        <div className="mt-2 flex items-center gap-2">
          <div className="flex items-center gap-1">
            {[1,2,3,4,5].map(i => (
              <div 
                key={i}
                className="w-1 bg-teal-400 rounded-full animate-pulse"
                style={{ 
                  height: `${8 + Math.random() * 12}px`,
                  animationDelay: `${i * 0.1}s`
                }}
              />
            ))}
          </div>
          <span className="text-xs text-teal-400">Now Playing</span>
        </div>
      )}
    </div>
  );
};

// Main Scanner Feeds Sidebar Component
const ScannerFeedsSidebar = ({ onScannerPlay }) => {
  const { token } = useAuth();
  const { toast } = useToast();
  const { currentScanner, isPlaying, playScanner, stopScanner, isLoading } = useAudio();
  
  const [scanners, setScanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [healthStats, setHealthStats] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  
  const headers = { 'Authorization': `Bearer ${token}` };
  
  // Fetch healthy scanners
  const fetchScanners = useCallback(async () => {
    if (!token) return;
    
    try {
      const [scannersRes, healthRes] = await Promise.all([
        fetch(`${API_URL}/api/streams/healthy`, { headers }),
        fetch(`${API_URL}/api/stream-health/status`, { headers })
      ]);
      
      if (scannersRes.ok) {
        const data = await scannersRes.json();
        setScanners(data.streams || []);
      }
      
      if (healthRes.ok) {
        const data = await healthRes.json();
        setHealthStats(data.status);
      }
    } catch (err) {
      console.error('Failed to fetch scanners:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);
  
  useEffect(() => {
    fetchScanners();
  }, [fetchScanners]);
  
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchScanners();
    setRefreshing(false);
    toast({
      title: 'Scanner feeds refreshed',
      description: `${scanners.length} streams available`
    });
  };
  
  // Play scanner using shared audio context
  const handlePlay = useCallback((scanner) => {
    // Notify parent to zoom map to scanner location
    if (onScannerPlay) {
      onScannerPlay(scanner);
    }
    
    // Play using shared audio context
    playScanner(scanner);
  }, [playScanner, onScannerPlay]);
  
  const handleStop = useCallback(() => {
    stopScanner();
  }, [stopScanner]);
  
  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-teal-400" />
      </div>
    );
  }
  
  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-slate-800">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Radio className="w-5 h-5 text-teal-500" />
            <h2 className="text-lg font-bold uppercase tracking-wide text-slate-200">
              Scanner Feeds
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="bg-emerald-900/50 text-emerald-300 font-mono">
              {healthStats?.healthy || scanners.length} live
            </Badge>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="p-1.5 rounded hover:bg-slate-800 text-slate-400 hover:text-white transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
        
        {/* Health bar */}
        {healthStats && (
          <div className="mb-3">
            <div className="flex items-center justify-between text-xs text-slate-400 mb-1">
              <span>Network Health</span>
              <span className="text-emerald-400 font-mono">{healthStats.health_percentage}%</span>
            </div>
            <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-emerald-500 transition-all duration-500"
                style={{ width: `${healthStats.health_percentage}%` }}
              />
            </div>
            <div className="flex justify-between text-[10px] text-slate-500 mt-1">
              <span>{healthStats.healthy}/{healthStats.total} streams</span>
              <span>{healthStats.states_covered} states</span>
            </div>
          </div>
        )}
        
        {/* Now Playing */}
        {currentScanner && (
          <div className="p-3 rounded-lg bg-teal-950/50 border border-teal-500/30">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {isLoading ? (
                  <Loader2 className="w-4 h-4 text-teal-400 animate-spin" />
                ) : (
                  <div className="flex items-center gap-1">
                    {[1,2,3,4,5].map(i => (
                      <div 
                        key={i}
                        className="w-1 bg-teal-400 rounded-full animate-pulse"
                        style={{ 
                          height: `${6 + Math.random() * 10}px`,
                          animationDelay: `${i * 0.15}s`
                        }}
                      />
                    ))}
                  </div>
                )}
                <div>
                  <p className="text-sm font-medium text-teal-300">{currentScanner.name}</p>
                  <p className="text-[10px] text-teal-400/70">{currentScanner.city}, {currentScanner.state}</p>
                </div>
              </div>
              <button
                onClick={handleStop}
                className="p-2 rounded-full bg-teal-500 text-white hover:bg-teal-400"
              >
                <Pause className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* Scanner List */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {scanners.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <Radio className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>No scanner feeds available</p>
          </div>
        ) : (
          scanners.map(scanner => (
            <ScannerFeedCard
              key={scanner.id}
              scanner={scanner}
              isPlaying={currentScanner?.id === scanner.id}
              onPlay={handlePlay}
              onStop={handleStop}
            />
          ))
        )}
      </div>
      
      {/* Footer */}
      <div className="p-3 border-t border-slate-800 bg-slate-900/50">
        <p className="text-[10px] text-slate-500 text-center">
          Real-time audio from community scanner feeds
        </p>
      </div>
    </div>
  );
};

export default ScannerFeedsSidebar;
